/**
 * 虎頭槽 — 依繡像參考用 primitive 重建（img2threejs 路數）。
 * 插座：頸根在 origin，+X 朝前，+Y 朝上。側視要認得出是虎。
 * 色只准礦物六色；平光；少面。不是寫實獸頭。
 */

import { cloneElement, isValidElement, useMemo, type ReactElement, type ReactNode } from 'react'
import * as THREE from 'three/webgpu'
import { EMBROIDERY, defaultBeastForm } from '../partLibrary'
import type { BeastForm } from '../types'

function mat(color: string) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.96,
    metalness: 0,
    flatShading: true,
    emissive: new THREE.Color(color),
    emissiveIntensity: 0.06,
  })
}

const outlineMat = new THREE.MeshBasicMaterial({
  color: EMBROIDERY.ink,
  side: THREE.BackSide,
})

function Stroke({
  children,
  fat = 1.045,
  ...props
}: { children: ReactNode; fat?: number } & Record<string, unknown>) {
  const geo = isValidElement(children) ? children as ReactElement : null
  return (
    <group>
      <mesh {...props}>{geo}</mesh>
      <mesh {...props} material={outlineMat} scale={fat}>
        {geo ? cloneElement(geo) : null}
      </mesh>
    </group>
  )
}

/** 掛在娃娃頭槽：頸窩對齊現有 [0.66, 0.3, 0] */
export function HuHead({ clay }: { clay?: BeastForm }) {
  const form = { ...defaultBeastForm(), ...clay }
  const tint = form.tint
  const ochre = useMemo(() => mat(tint ?? EMBROIDERY.ochre), [tint])
  const clam = useMemo(() => mat(EMBROIDERY.clam), [])
  const cinnabar = useMemo(() => mat(EMBROIDERY.cinnabar), [])
  const ink = useMemo(() => mat(EMBROIDERY.ink), [])
  const snout = form.snout
  const ears = form.earSpread

  return (
    <group position={[0.64, 0.28, 0]} userData={{ socket: 'head', source: 'hu' }}>
      {/* 頸根：插進身槽 */}
      <Stroke material={ochre} position={[-0.02, 0.02, 0]} rotation={[0, 0, 0.15]}>
        <cylinderGeometry args={[0.07, 0.085, 0.12, 8]} />
      </Stroke>

      {/* 圓顱 */}
      <Stroke material={ochre} position={[0.07, 0.16, 0]}>
        <sphereGeometry args={[0.2, 10, 8]} />
      </Stroke>

      {/* 蛤粉吻 */}
      <Stroke material={clam} position={[0.1 + 0.06 * snout, 0.05, 0]} scale={[1.12 * snout, 0.72, 1.06]}>
        <sphereGeometry args={[0.125, 9, 7]} />
      </Stroke>

      {/* 朱砂鼻 */}
      <mesh material={cinnabar} position={[0.18 + 0.09 * snout, 0.075, 0]}>
        <sphereGeometry args={[0.032, 6, 5]} />
      </mesh>

      {/* 耳：外赭、內朱 */}
      <group position={[-0.01, 0.32, 0.08 * ears + 0.045]} rotation={[0.15, 0.15, 0.2]}>
        <Stroke material={ochre}>
          <sphereGeometry args={[0.072, 8, 6]} />
        </Stroke>
        <mesh material={cinnabar} position={[0.018, 0.004, 0.012]} scale={[0.72, 0.72, 0.45]}>
          <sphereGeometry args={[0.055, 7, 5]} />
        </mesh>
      </group>
      <group position={[-0.01, 0.32, -(0.08 * ears + 0.045)]} rotation={[-0.15, -0.15, 0.2]}>
        <Stroke material={ochre}>
          <sphereGeometry args={[0.072, 8, 6]} />
        </Stroke>
        <mesh material={cinnabar} position={[0.018, 0.004, -0.012]} scale={[0.72, 0.72, 0.45]}>
          <sphereGeometry args={[0.055, 7, 5]} />
        </mesh>
      </group>

      {/* 墨眼＋蛤粉高光 */}
      <mesh material={ink} position={[0.18, 0.175, 0.072]}>
        <sphereGeometry args={[0.026, 6, 5]} />
      </mesh>
      <mesh material={ink} position={[0.18, 0.175, -0.072]}>
        <sphereGeometry args={[0.026, 6, 5]} />
      </mesh>
      <mesh material={clam} position={[0.195, 0.188, 0.08]}>
        <sphereGeometry args={[0.008, 5, 4]} />
      </mesh>
      <mesh material={clam} position={[0.195, 0.188, -0.08]}>
        <sphereGeometry args={[0.008, 5, 4]} />
      </mesh>

      {/* 額心墨斑 */}
      <mesh material={ink} position={[0.1, 0.3, 0]} rotation={[0, 0, 0.2]}>
        <capsuleGeometry args={[0.016, 0.07, 3, 5]} />
      </mesh>
      {/* 兩頰三撇 */}
      {([-1, 1] as const).flatMap(side => (
        [0.02, -0.02, -0.06].map((y, i) => (
          <mesh
            key={`whisker-${side}-${i}`}
            material={ink}
            position={[0.2, 0.1 + y, 0.11 * side]}
            rotation={[0.15 * side, 0.35 * side, 0.15 * (i - 1)]}
          >
            <boxGeometry args={[0.07, 0.012, 0.012]} />
          </mesh>
        ))
      ))}
      {/* 顱頂兩筆 */}
      <mesh material={ink} position={[0.02, 0.33, 0.06]} rotation={[0.4, 0, 0.3]}>
        <capsuleGeometry args={[0.012, 0.05, 3, 5]} />
      </mesh>
      <mesh material={ink} position={[0.02, 0.33, -0.06]} rotation={[-0.4, 0, 0.3]}>
        <capsuleGeometry args={[0.012, 0.05, 3, 5]} />
      </mesh>
    </group>
  )
}
